/**
 * Conquest Audit Credits — Reseller (SUB_MASTER) only.
 * Two purchase options linking out to Wave checkout, plus a "Reports and
 * Credits" section: balance, a "Request a Report" form (dealer name +
 * website), the resulting dealer list (pending vs fulfilled), and recent
 * credit activity so the reseller can confirm we only deducted what they
 * requested.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { CreditCard, ExternalLink, FileText, PlayCircle, Clock, ShieldAlert } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CHECKOUT_URLS = {
  10: "https://next.waveapps.com/checkouts/8135377352f24b6aa7216ce9eaad6cc9",
  20: "https://next.waveapps.com/checkouts/99eb6f1c0049418ea39fde85090de936",
};

const CreditOptionCard = ({ count, testId }) => (
  <a
    href={CHECKOUT_URLS[count]}
    target="_blank"
    rel="noreferrer"
    className="flex-1 group flex flex-col items-center gap-3 rounded-xl border-2 border-gray-200 bg-white px-8 py-10 text-center transition-all hover:border-indigo-400 hover:shadow-lg"
    data-testid={testId}
  >
    <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
      <CreditCard className="w-6 h-6 text-indigo-600" />
    </div>
    <div>
      <div className="text-2xl font-bold text-gray-900">{count} Audits</div>
      <div className="text-[12px] text-gray-500 mt-1">Checkout securely via Wave</div>
    </div>
    <span className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-indigo-600 group-hover:text-indigo-700">
      Buy now <ExternalLink className="w-3.5 h-3.5" />
    </span>
  </a>
);

const DealerReportRow = ({ dealer }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
       data-testid={`conquest-dealer-report-row-${dealer.id}`}>
    <div>
      <span className="font-medium text-gray-900 text-sm">{dealer.name}</span>
      {dealer.status === "requested" && (
        <div className="flex items-center gap-1 text-[11.5px] text-amber-600 mt-0.5">
          <Clock className="w-3 h-3" /> Pending — we&apos;ll follow up soon
        </div>
      )}
      {dealer.requested_by_email && (
        <div className="text-[11px] text-gray-400 mt-0.5" data-testid={`conquest-dealer-requested-by-${dealer.id}`}>
          Requested by: {dealer.requested_by_email}
        </div>
      )}
    </div>
    <div className="flex gap-2">
      {dealer.view_report_url && (
        <a href={dealer.view_report_url} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
           data-testid={`conquest-dealer-view-report-${dealer.id}`}>
          <FileText className="w-3.5 h-3.5" /> View Report
        </a>
      )}
      {dealer.demo_url && (
        <a href={dealer.demo_url} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
           data-testid={`conquest-dealer-demo-${dealer.id}`}>
          <PlayCircle className="w-3.5 h-3.5" /> Demo
        </a>
      )}
    </div>
  </div>
);

const RESELLER_ROLE_LABEL = { admin: "Admin", user: "User", viewer: "Viewer" };

const ConquestAuditCreditsPage = () => {
  const { apiFetch } = useAuth();
  const { success: toastOk, error: toastErr } = useToast();
  const [summary, setSummary] = useState({ balance: 0, dealers: [] });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(null); // null = not denied, else the backend's detail message
  const [reqForm, setReqForm] = useState({ dealer_name: "", website_url: "" });
  const [submittingReq, setSubmittingReq] = useState(false);

  // Reseller switcher (cross-grant Viewer recipients only — everyone else
  // has exactly one entry here and never sees the dropdown at all).
  const [resellerOptions, setResellerOptions] = useState([]);
  const [selectedResellerId, setSelectedResellerId] = useState(null);

  useEffect(() => {
    apiFetch("/submaster/conquest/my-resellers").then((res) => {
      const options = res.data?.resellers || [];
      setResellerOptions(options);
      const home = options.find((o) => !o.is_cross_grant);
      setSelectedResellerId((home || options[0])?.reseller_business_id || null);
    }).catch(() => setResellerOptions([]));
  }, [apiFetch]);

  const selectedOption = resellerOptions.find((o) => o.reseller_business_id === selectedResellerId);
  const isViewingForeignReseller = !!selectedOption?.is_cross_grant;

  const load = useCallback(() => {
    if (!selectedResellerId) return;
    setLoading(true);
    setAccessDenied(null);
    const qs = `?reseller_id=${selectedResellerId}`;
    const calls = [apiFetch(`/submaster/conquest/summary${qs}`)];
    // Credit history is never available for a cross-grant Viewer (Stage
    // 3's matrix excludes (viewer, is_cross_grant=True) on purpose) — skip
    // the call entirely rather than let it 403 and log noise.
    if (!isViewingForeignReseller) calls.push(apiFetch(`/submaster/conquest/credit-history${qs}`));

    Promise.all(calls).then(([summaryRes, historyRes]) => {
      setSummary({ balance: summaryRes.data.balance || 0, dealers: summaryRes.data.dealers || [] });
      setHistory(historyRes?.data.history || []);
    }).catch((err) => {
      // Distinguish "you genuinely have no Conquest access here" (403)
      // from any other failure — a silent zero-balance/empty-dealers
      // state looks identical to "nothing set up yet" otherwise, which
      // hid the Stage 3 backfill regression (found 2026-09-24: Sharper
      // Aim's reseller admin saw an empty page instead of an actionable
      // error when her role row was missing).
      if (err.response?.status === 403) {
        setAccessDenied(err.response?.data?.detail || "You do not have Conquest Audit access for this reseller.");
      }
      setSummary({ balance: 0, dealers: [] });
      setHistory([]);
    }).finally(() => setLoading(false));
  }, [apiFetch, selectedResellerId, isViewingForeignReseller]);

  useEffect(() => { load(); }, [load]);

  const submitReportRequest = async () => {
    if (!reqForm.dealer_name.trim() || !reqForm.website_url.trim()) {
      toastErr("Enter both the dealer name and website address");
      return;
    }
    try {
      setSubmittingReq(true);
      await apiFetch("/submaster/conquest/report-requests", {
        method: "POST",
        data: { dealer_name: reqForm.dealer_name.trim(), website_url: reqForm.website_url.trim() },
      });
      toastOk("Report requested — we'll follow up soon");
      setReqForm({ dealer_name: "", website_url: "" });
      load();
    } catch (err) {
      toastErr(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmittingReq(false);
    }
  };

  return (
    <div className="p-6 w-full" data-testid="conquest-audit-credits-page">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <CreditCard className="w-5 h-5 text-indigo-600" />
          </div>
          <h1 className="text-[20px] font-bold text-gray-900">Conquest Audit Credits</h1>
        </div>
        <p className="text-[13px] text-gray-500 ml-11">
          Purchase Conquest Audit credits. Choose a bundle below to check out.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 max-w-2xl mb-8">
        <CreditOptionCard count={10} testId="conquest-audit-credits-buy-10" />
        <CreditOptionCard count={20} testId="conquest-audit-credits-buy-20" />
      </div>

      <div className="max-w-2xl border-t border-gray-200 pt-6" data-testid="conquest-reports-credits-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[15px] font-bold text-gray-900">Reports and Credits</h2>
          {resellerOptions.length > 1 && (
            <div className="flex items-center gap-2" data-testid="conquest-reseller-switcher">
              <Label htmlFor="conquest_reseller_switcher" className="text-xs text-gray-500 whitespace-nowrap">
                Viewing data for:
              </Label>
              <Select value={selectedResellerId || ""} onValueChange={setSelectedResellerId}>
                <SelectTrigger id="conquest_reseller_switcher" className="w-[220px]" data-testid="conquest-reseller-switcher-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {resellerOptions.map((o) => (
                    <SelectItem key={o.reseller_business_id} value={o.reseller_business_id} data-testid={`conquest-reseller-option-${o.reseller_business_id}`}>
                      {o.reseller_name} — {RESELLER_ROLE_LABEL[o.role] || o.role} {o.is_cross_grant ? "(Cross-Reseller)" : "(Home)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {!loading && accessDenied ? (
          <div
            className="flex items-start gap-3 p-4 rounded-lg border border-amber-300 bg-amber-50 mb-6"
            data-testid="conquest-access-denied-banner"
          >
            <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-900">You don&apos;t have Conquest Audit access</p>
              <p className="text-[13px] text-amber-700 mt-0.5">{accessDenied} Ask your account admin to grant you access.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-5 p-4 bg-indigo-50 rounded-lg flex items-center justify-between">
              <span className="text-sm font-medium text-indigo-900">
                {isViewingForeignReseller ? `${selectedOption.reseller_name}'s Audit Credit Balance` : "Your Audit Credit Balance"}
              </span>
              <span className="text-2xl font-bold text-indigo-900" data-testid="conquest-my-credit-balance">
                {loading ? "…" : summary.balance}
              </span>
            </div>

            {isViewingForeignReseller ? (
              <div
                className="mb-6 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-500"
                data-testid="conquest-foreign-reseller-note"
              >
                Request a Report and Credit Activity are only available for your home reseller — you have
                Viewer-only access to {selectedOption.reseller_name}&apos;s data.
              </div>
            ) : (
              <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200" data-testid="conquest-request-report-form">
                <h3 className="text-[13px] font-semibold text-gray-700 mb-3">Request a Report</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="req_dealer_name" className="text-xs">Dealer Name</Label>
                    <Input id="req_dealer_name" placeholder="e.g., ABC Motors" value={reqForm.dealer_name}
                           onChange={(e) => setReqForm(f => ({ ...f, dealer_name: e.target.value }))}
                           data-testid="conquest-request-dealer-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="req_website_url" className="text-xs">Website Address</Label>
                    <Input id="req_website_url" placeholder="https://dealersite.com" value={reqForm.website_url}
                           onChange={(e) => setReqForm(f => ({ ...f, website_url: e.target.value }))}
                           data-testid="conquest-request-website-input" />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-[11.5px] text-gray-400">
                    {summary.balance <= 0
                      ? "You're out of audit credits — contact your account admin to add more before requesting another report."
                      : "Requests are processed during business hours (Mon–Fri, excluding holidays)."}
                  </p>
                  <Button onClick={submitReportRequest} disabled={submittingReq || summary.balance <= 0} data-testid="conquest-submit-request-btn">
                    {submittingReq ? "Submitting…" : "Request Report"}
                  </Button>
                </div>
              </div>
            )}

            <h3 className="text-[13px] font-semibold text-gray-600 uppercase tracking-wide mb-2">Dealers</h3>
            {loading ? (
              <p className="text-sm text-gray-400 py-4">Loading…</p>
            ) : summary.dealers.length === 0 ? (
              <p className="text-sm text-gray-500 py-4" data-testid="no-conquest-reports-message">
                No dealer reports have been set up yet.
              </p>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 px-4 mb-6" data-testid="conquest-reports-dealer-list">
                {summary.dealers.map((d) => <DealerReportRow key={d.id} dealer={d} />)}
              </div>
            )}

            {!isViewingForeignReseller && history.length > 0 && (
              <>
                <h3 className="text-[13px] font-semibold text-gray-600 uppercase tracking-wide mb-2">Recent Credit Activity</h3>
                <div className="bg-white rounded-lg border border-gray-200 divide-y" data-testid="conquest-my-credit-history">
                  {history.map((h, i) => (
                    <div key={i} className="px-3 py-2 flex items-center justify-between text-sm gap-3">
                      <span className={h.delta > 0 ? "text-green-700 font-medium" : "text-red-700 font-medium"}>
                        {h.delta > 0 ? "+" : ""}{h.delta} credit{Math.abs(h.delta) === 1 ? "" : "s"}
                      </span>
                      <span className="text-gray-600 truncate">{h.dealer_name || "—"}</span>
                      <span className="text-gray-500 text-xs truncate">{h.created_by_email || ""}</span>
                      <span className="text-gray-400 text-xs flex-shrink-0">
                        {h.created_at ? new Date(h.created_at).toLocaleDateString() : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConquestAuditCreditsPage;
